/* global gtag */
/* global fbq */
import React, { useState, useContext } from 'react';
import axios from 'axios';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useLocation } from 'react-router-dom';
import { getStoredGclid, getStoredAdParams } from '../utils/adTracking';


// redirectTo: where to send the user after signup (default: the create tool at "/")
// onSuccess (optional): when provided, the parent owns what happens after auth
// (e.g. keep the user in place and resume their work). Signup will NOT navigate
// or call onClose itself in that case.
function Signup({ onClose, redirectTo = '/', onSuccess }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const { login, setUser, googleLogin } = useContext(AuthContext);
  const navigate = useNavigate();

  // Regex for validating a single-word username
  const usernameRegex = /^\w+$/;
  const location = useLocation();
  const previousPage = location.state?.from || '/';

  function gtagSendEvent(url) {
    let callbackTriggered = false;

    const callback = function () {
      if (!callbackTriggered) {
        callbackTriggered = true;
        if (typeof url === 'string') {
          window.location = url;
        }
      }
    };

    gtag('event', 'conversion_event_signup', {
      event_callback: callback,
      event_timeout: 2000,
    });

    setTimeout(() => {
      if (!callbackTriggered) {
        callbackTriggered = true;
        if (typeof url === 'string') {
          window.location = url;
        }
      }
    }, 2000);

    return false;
  }

  // Handle username validation
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (!usernameRegex.test(value)) {
      setUsernameError('Username must be a single word without spaces or special characters.');
    } else {
      setUsernameError('');
    }
  };

  // Standard form‐based signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (usernameError) {
      setMessage('Please fix the errors before submitting.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://koi-2028.onrender.com/register', {
        username,
        email,
        password,
      });

      // Track conversion with ad attribution data
      const gclid = getStoredGclid();
      const adParams = getStoredAdParams();

      fbq('track', 'CompleteRegistration', { value: 0, currency: 'USD' });
      gtag('event', 'conversion_event_signup', {
        ...(gclid && { gclid }), // Include gclid if user came from Google Ads
        ...(adParams.utm_source && { traffic_source: adParams.utm_source }),
      });

      // Auto‐login
      const loginResult = await login(email, password);
      if (loginResult.success) {
        if (onSuccess) {
          // Parent keeps the user in place (e.g. resumes their design). No nav.
          onSuccess();
        } else {
          setMessage('Signup successful! Redirecting...');
          navigate(redirectTo);
          onClose();
        }
      } else {
        setMessage(loginResult.message || 'Signup succeeded but login failed.');
      }
    } catch (error) {
      console.error('Signup failed:', error);
      setMessage(error.response?.data?.error || 'An error occurred during signup.');
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth signup
  const handleGoogleSignupSuccess = async (response) => {
  setLoading(true);
  setMessage('');

  try {
    // 1) Hit your callback endpoint and let it both create & log in the user.
    const { data } = await axios.post(
      'https://koi-2028.onrender.com/auth/google/callback',
      { token: response.credential },
      { withCredentials: true }               // ← ensure cookie comes back
    );

    // 2) data should be { message: 'Login successful', user: { … } }
    const newUser = data.user;
    if (!newUser) {
      throw new Error('No user returned from signup endpoint');
    }

    // 3) Shove it into context & localStorage yourself
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));

    // Track signup conversion for Google OAuth users with ad attribution
    const gclid = getStoredGclid();
    const adParams = getStoredAdParams();

    fbq('track', 'CompleteRegistration', { value: 0, currency: 'USD' });
    gtag('event', 'conversion_event_signup', {
      ...(gclid && { gclid }), // Include gclid if user came from Google Ads
      ...(adParams.utm_source && { traffic_source: adParams.utm_source }),
    });

    if (onSuccess) {
      // Parent keeps the user in place (e.g. resumes their design). No nav —
      // this also fixes the previous bug where Google signup ejected the user
      // to the homepage and wiped their in-progress design.
      onSuccess();
    } else {
      setMessage('Signup & login successful! Redirecting…');
      setTimeout(() => {
        onClose();
        navigate(previousPage);
      }, 1000);
    }

  } catch (err) {
    console.error('Google signup/login error:', err);
    setMessage(
      err.response?.data?.error ||
      err.message ||
      'Something went wrong. Please try again.'
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <GoogleOAuthProvider clientId="513619425094-tuoekhjks9mdb6cjoifrdc7m39g96vsa.apps.googleusercontent.com">
      <div className="flex items-center justify-center">
        <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-lg relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
              <div className="loader">Loading...</div>
            </div>
          )}

          <h2 className="text-2xl font-bold text-center text-gray-700">Signup</h2>
          <form onSubmit={handleSignup} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                value={username}
                onChange={handleUsernameChange}
                required
                className={`w-full px-3 py-2 mt-1 text-gray-900 bg-gray-200 border ${
                  usernameError ? 'border-red-500' : 'border-gray-300'
                } rounded-lg focus:outline-none focus:ring-2 ${
                  usernameError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'
                }`}
              />
              {usernameError && <p className="mt-1 text-sm text-red-500">{usernameError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 mt-1 text-gray-900 bg-gray-200 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 mt-1 text-gray-900 bg-gray-200 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <button
                type="submit"
                className="w-full px-4 py-2 font-medium text-white bg-rose-500 rounded-lg hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                disabled={loading || !!usernameError}
              >
                Signup
              </button>
            </div>
          </form>

          <div className="flex items-center justify-center mt-4 space-x-2">
            <span className="text-sm font-medium text-gray-500">Or</span>
          </div>

          <div className="flex items-center justify-center mt-4">
            <GoogleLogin
              onSuccess={handleGoogleSignupSuccess}
              onError={() => setMessage('Google signup failed.')}
              text="signup_with"
              className="w-full"
            />
          </div>

          {message && <div className="mt-4 text-center text-rose-500">{message}</div>}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}

export default Signup;
